var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { arrow, autoUpdate, flip, FloatingNode, FloatingPortal, FloatingTree, hide, offset, safePolygon, shift, size, useClick, useDismiss, useFloating, useFloatingNodeId, useFloatingParentNodeId, useFocus, useHover, useInteractions, } from '@floating-ui/react';
import cn from 'classnames';
import { useCallback, useEffect, useRef } from 'react';
import { useUncontrolledProp } from 'uncontrollable';
import { extractSupportProps, isBrowser } from '@snack-uikit/utils';
import { DEFAULT_FALLBACK_PLACEMENTS, PLACEMENT, POPOVER_HEIGHT_STRATEGY, POPOVER_WIDTH_STRATEGY, } from '../../constants';
import { getArrowOffset, getPopoverRootElement, getPopoverTriggerJSX, getTriggerProps, mapPopoverActionsToSynthetic, referenceActionToEvent, stopPropagationMouse, stopPropagationTouch, } from '../../utils';
import { Arrow } from '../Arrow';
import { useOffset } from './hooks';
import styles from './styles.module.css';
function PopoverPrivateComponent(_a) {
    var _b;
    var { className, triggerClassName, children, open: openProp, onOpenChange, placement: placementProp = PLACEMENT.Top, hasArrow, offset: offsetProp, popoverContent, trigger, outsideClick, hoverDelayOpen, hoverDelayClose, triggerRef, widthStrategy = POPOVER_WIDTH_STRATEGY.Auto, heightStrategy = POPOVER_HEIGHT_STRATEGY.Auto, closeOnEscapeKey = true, triggerClickByKeys = true, fallbackPlacements = DEFAULT_FALLBACK_PLACEMENTS, arrowContainerClassName, arrowElementClassName, disableSpanWrapper = false } = _a, rest = __rest(_a, ["className", "triggerClassName", "children", "open", "onOpenChange", "placement", "hasArrow", "offset", "popoverContent", "trigger", "outsideClick", "hoverDelayOpen", "hoverDelayClose", "triggerRef", "widthStrategy", "heightStrategy", "closeOnEscapeKey", "triggerClickByKeys", "fallbackPlacements", "arrowContainerClassName", "arrowElementClassName", "disableSpanWrapper"]);
    const arrowRef = useRef(null);
    const [isOpen, setIsOpen] = useUncontrolledProp(openProp, false, onOpenChange);
    const nodeId = useFloatingNodeId();
    const offsetValue = useOffset({ triggerClassName, offsetProp });
    const arrowOffset = getArrowOffset(arrowRef.current);
    const { floatingStyles, refs, context, middlewareData, placement } = useFloating({
        nodeId,
        placement: placementProp,
        open: isOpen,
        onOpenChange: setIsOpen,
        whileElementsMounted: autoUpdate,
        middleware: [
            shift(),
            offset(offsetValue + arrowOffset),
            hasArrow && arrow({ element: arrowRef, padding: (offsetValue + arrowOffset) * 2 }),
            flip({
                fallbackPlacements,
            }),
            size({
                apply({ rects, availableHeight }) {
                    const floating = refs.floating.current;
                    if (!floating)
                        return;
                    switch (heightStrategy) {
                        case POPOVER_HEIGHT_STRATEGY.Eq:
                            floating.style.height = `${availableHeight}px`;
                            break;
                        case POPOVER_HEIGHT_STRATEGY.Lte:
                            floating.style.maxHeight = `${availableHeight}px`;
                            break;
                        case POPOVER_HEIGHT_STRATEGY.Auto:
                        default:
                            break;
                    }
                    switch (widthStrategy) {
                        case POPOVER_WIDTH_STRATEGY.Eq:
                            floating.style.width = `${rects.reference.width}px`;
                            floating.style.minWidth = '0px';
                            break;
                        case POPOVER_WIDTH_STRATEGY.Gte:
                            floating.style.width = `auto`;
                            floating.style.minWidth = `${rects.reference.width}px`;
                            break;
                        case POPOVER_WIDTH_STRATEGY.Auto:
                        default:
                            floating.style.width = `auto`;
                            floating.style.minWidth = 'auto';
                    }
                },
            }),
            hide(),
        ],
    });
    const setReference = useCallback(node => {
        refs.setReference(node);
        if (triggerRef) {
            typeof triggerRef === 'function' ? triggerRef(node) : (triggerRef.current = node);
        }
    }, [triggerRef, refs]);
    const { useHoverTrigger, useFocusTrigger, useClickTrigger, keyboardOnly } = getTriggerProps(trigger);
    const dismiss = useDismiss(context, {
        outsidePress: outsideClick,
        ancestorScroll: false,
        escapeKey: closeOnEscapeKey,
    });
    const hover = useHover(context, {
        enabled: useHoverTrigger,
        handleClose: safePolygon({ requireIntent: false }),
        delay: { open: hoverDelayOpen, close: hoverDelayClose },
    });
    const focus = useFocus(context, { enabled: useFocusTrigger, visibleOnly: keyboardOnly });
    const click = useClick(context, { enabled: useClickTrigger, keyboardHandlers: triggerClickByKeys });
    const { getFloatingProps, getReferenceProps } = useInteractions([dismiss, hover, focus, click]);
    const addOrRemoveTriggerRefEvents = useCallback((actions, type) => {
        if (typeof triggerRef === 'object' && (triggerRef === null || triggerRef === void 0 ? void 0 : triggerRef.current)) {
            refs.setReference(triggerRef.current);
            Object.entries(actions).map(([key, value]) => {
                const eventKey = referenceActionToEvent(key);
                if (eventKey && isBrowser()) {
                    type === 'add' && triggerRef.current.addEventListener(eventKey, value);
                    type === 'remove' && triggerRef.current.removeEventListener(eventKey, value);
                }
            });
        }
    }, [refs, triggerRef]);
    useEffect(() => {
        if (children) {
            return;
        }
        const actions = mapPopoverActionsToSynthetic(getReferenceProps());
        addOrRemoveTriggerRefEvents(actions, 'add');
        return () => addOrRemoveTriggerRefEvents(actions, 'remove');
    }, [children, addOrRemoveTriggerRefEvents, getReferenceProps]);
    const portal = isOpen && (_jsx(FloatingPortal, { root: getPopoverRootElement(), children: _jsxs("div", Object.assign({ role: 'presentation' }, extractSupportProps(rest), { className: cn(styles.floating, className, {
                [styles.floatingHidden]: Boolean((_b = middlewareData.hide) === null || _b === void 0 ? void 0 : _b.referenceHidden),
            }), ref: refs.setFloating, style: floatingStyles, "data-placement": placement }, getFloatingProps({
            onClick: stopPropagationMouse,
            onMouseDown: stopPropagationMouse,
            onMouseUp: stopPropagationMouse,
            onTouchStart: stopPropagationTouch,
            onTouchEnd: stopPropagationTouch,
            onTouchMove: stopPropagationTouch,
        }), { children: [popoverContent, middlewareData.arrow && (_jsx(Arrow, { placement: placement, x: middlewareData.arrow.x, y: middlewareData.arrow.y, arrowContainerClassName: arrowContainerClassName, arrowElementClassName: arrowElementClassName, arrowRef: arrowRef }))] })) }, 'portal'));
    return (_jsxs(FloatingNode, { id: nodeId, children: [children &&
                getPopoverTriggerJSX({
                    validElementWrapperClassName: cn(triggerClassName),
                    getReferenceProps,
                    children,
                    setReference,
                    disableSpanWrapper,
                }), portal] }));
}
export function PopoverPrivate(_a) {
    var { children } = _a, props = __rest(_a, ["children"]);
    const parentNodeId = useFloatingParentNodeId();
    const isTreeRoot = parentNodeId === null;
    if (isTreeRoot) {
        return (_jsx(FloatingTree, { children: _jsx(PopoverPrivateComponent, Object.assign({}, props, { children: children })) }));
    }
    return _jsx(PopoverPrivateComponent, Object.assign({}, props, { children: children }));
}
